import { CalendarListing, Experience, Listing, ListingStatus } from '@/types';
import { formatPricing, formatTimeRange } from '@/utils';
import { ThemeOptions } from '@silverstein-properties/inspirelabs-ui';

export const getCalendarListingBackgroundColor = (
  status: string,
  purchasedBy: Array<string | { consumerId?: string; name?: string }>,
  themeOptions: ThemeOptions
) => {
  if (status === ListingStatus.MERCHANT_CANCELLED) {
    return {
      bgColor: themeOptions!.palette!.error!.light,
      borderColor: themeOptions!.palette!.error!.main,
    };
  } else if (purchasedBy?.length > 0) {
    return {
      bgColor: themeOptions!.palette!.secondary!.main,
      borderColor: themeOptions!.palette!.secondary!.light,
    };
  } else {
    return {
      bgColor: themeOptions!.palette!.secondary!.light,
      borderColor: themeOptions!.palette!.secondary!.faint,
    };
  }
};

export const formatListing = (
  listings: Listing[],
  experiences: Experience[]
): CalendarListing[] => {
  return (
    listings?.map(listing => ({
      id: listing.id || '',
      title:
        listing.basePrice?.totalAmount || listing.totalAmount
          ? formatPricing(listing.basePrice?.totalAmount || listing.totalAmount)
          : '',
      end: listing.endsAt || '',
      start: listing.startsAt || '',
      timeRange: formatTimeRange(listing.startsAt, listing.endsAt),
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      classNames:
        listing?.status === ListingStatus.MERCHANT_CANCELLED
          ? 'cancelled-listing'
          : '',
      nbOfGuests: listing?.purchasedBy?.length || 0,
      nbOfCancellations: 0,
      capacity:
        listing.capacity?.max ||
        experiences?.find(x => x.id === listing.productId)?.capacity?.max ||
        listing.productSnapshot?.capacity?.max ||
        0,
      bookings: listing.bookings
        ?.filter(booking => booking.status === 'ACTIVE')
        .map(item => ({
          consumerId: item.consumerId,
          name: `${item.consumer?.firstName} ${item.consumer?.lastName}`,
        })),
      reccuringEvent: listing?.reccuringEvents?.[0],
      productId: listing.productId,
      host: listing.host,
      status: listing.status,
      productSnapshot: listing.productSnapshot,
      isRecurringEvent: Boolean(listing?.recurringEventId),
      hasBookings: listing.hasBookings,
    })) || []
  );
};

export const doEventsOverlap = (
  event1: CalendarListing,
  event2: CalendarListing
) => {
  const start1 = new Date(event1.start).getTime();
  const end1 = new Date(event1.end).getTime();
  const start2 = new Date(event2.start).getTime();
  const end2 = new Date(event2.end).getTime();

  // Events overlap if one event starts before the other ends
  return start1 < end2 && start2 < end1;
};

// Function to group events based on time overlap
// Function to group events based on time overlap with main event
// Function to group events based on any time overlap
export const groupEventsByTimeOverlap = (
  originalEvents: CalendarListing[]
): CalendarListing[] => {
  if (originalEvents.length === 0) {
    return [];
  }
  // Clone and sort events by start time
  const sortedEvents = [...originalEvents].sort((a, b) => {
    const durationA = new Date(a.end).getTime() - new Date(a.start).getTime();
    const durationB = new Date(b.end).getTime() - new Date(b.start).getTime();
    return durationB - durationA; // Sort by duration (longest first)
  });

  // Create groups based on overlapping times
  const eventGroups = [];
  let currentGroup = [sortedEvents[0]];

  // Group overlapping events
  for (let i = 1; i < sortedEvents.length; i++) {
    const currentEvent = sortedEvents[i];

    // Check if this event overlaps with any event in the current group
    const hasOverlap = doEventsOverlap(currentGroup[0], currentEvent);

    if (hasOverlap) {
      // Add to current group if there's overlap
      currentGroup.push(currentEvent);
    } else {
      let isGroupExist = false;
      eventGroups.forEach(group => {
        if (doEventsOverlap(group[0], currentEvent)) {
          isGroupExist = true;
          group.push(currentEvent);
        }
      });
      if (!isGroupExist) {
        eventGroups.push([...currentGroup]);
        currentGroup = [currentEvent];
      }
    }
  }

  // Add the last group
  if (currentGroup.length > 0) {
    eventGroups.push(currentGroup);
  }

  // Convert groups to consolidated events
  return eventGroups.map(group => {
    const mainEvent = group[0];

    // Create consolidated event
    return {
      ...mainEvent,
      // Store original events for reference
      eventsGroup: group.slice(1),
    };
  });
};
